<template>
  <div class="w-full max-w-lg p-8">
    <!-- Regular Login Form -->
    <div v-if="!showTwoFactorInput">
      <h1 class="font-[700] text-3xl text-center text-title mb-[1vh]">{{
        $t('pages.login.step2.title') }}</h1>
      <p class="font-[600] text-base text-center align-middle text-body mb-[2vh]">
        {{ $t('pages.login.step2.subtitle') }}
      </p>
      <!--
        method="post"：hydration 完成前 @submit 還沒掛上，這時按下送出瀏覽器會走原生提交，
        預設的 GET 會把帳號密碼明文帶進網址列與瀏覽器歷史。搭配下方送出鈕的 isHydrated
        防呆，讓「JS 還沒接手」這段時間不會外洩憑證。
      -->
      <UForm method="post" :state="formState" :schema="schema" @submit="onSubmit" class="space-y-4">
        <UFormField name="email" label="Email">
          <UInput
            class="w-full"
            size="xl"
            v-model="formState.email"
            type="email"
            placeholder="Enter your email"
            :disabled="isLoading || isLoginWithSpecifiedEmailMode"
            :ui="{ base: 'py-3 px-4' }"
            @keydown.enter.prevent="handleEnterSubmit"
          />
        </UFormField>

        <UFormField name="password" label="Password">
          <UInput
            class="w-full"
            size="xl"
            v-model="formState.password"
            :type="isPasswordVisible ? 'text' : 'password'"
            placeholder="Enter your password"
            :disabled="isLoading"
            :ui="{ base: 'py-3 px-4', trailing: 'pr-5' }"
            @keydown.enter.prevent="handleEnterSubmit"
          >
            <template v-if="formState.password" #trailing>
              <UIcon :name="isPasswordVisible ? 'i-lucide-eye' : 'i-lucide-eye-off'" class="w-6 h-6"
                @click="isPasswordVisible = !isPasswordVisible" />
            </template>
          </UInput>
          <template #error="{ error }">
            <div class="flex justify-between items-center mt-1 w-full">
              <span class="text-error text-sm">{{ error }}</span>
              <NuxtLink :to="localePath('/forgot-password')"
                class="text-interactive hover:underline cursor-pointer text-sm">
                {{ $t('pages.login.step2.forgotPassword') }}
              </NuxtLink>
            </div>
          </template>
        </UFormField>
        <div>
          <div class="flex items-center my-4">
            <div class="flex-grow border-t border-default"></div>
            <span class="mx-2 font-[700] text-lg tracking-tight text-title">{{ $t('pages.login.step2.or') }}</span>
            <div class="flex-grow border-t border-default"></div>
          </div>
          <UButton type="button" color="neutral" variant="outline" size="xl" block :disabled="isLoading"
            @click="handleSocialLogin()" class="mb-[3vh] py-3">
            <div class="flex items-center justify-center gap-2">
              <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" class="w-5 h-5" />
              <div class="flex items-center justify-center gap-1">
                <div>{{ $t('pages.login.step2.continueWithGoogle.continue') }}</div>
                <div>{{ $t('pages.login.step2.continueWithGoogle.with') }}</div>
                <div>{{ $t('pages.login.step2.continueWithGoogle.google') }}</div>
              </div>
            </div>
          </UButton>
        </div>

        <UButton type="submit" color="primary" size="xl" class="py-3" block :loading="isLoading"
          :disabled="!isHydrated">
          {{ $t('pages.login.step2.continue') }}
        </UButton>

        <p v-if="!isLoginWithSpecifiedEmailMode" class="text-center text-sm font-bold">
          {{ $t('pages.login.step2.noAccount') }}
          <template v-if="isLoading">
            <span class="text-body font-bold ml-1">{{ $t('pages.login.step2.signup') }}</span>
          </template>
          <template v-else>
            <NuxtLink :to="localePath('/signup')" class="text-interactive hover:underline font-bold cursor-pointer ml-1">
              {{ $t('pages.login.step2.signup') }}
            </NuxtLink>
          </template>
        </p>
      </UForm>
    </div>

    <!-- Two-Factor Authentication Form -->
    <TwoFactorLogin v-else :provisional-token="provisionalToken" @verification-success="handleTwoFactorSuccess"
      @back-to-login="backToLogin" />
  </div>
</template>

<script setup lang="ts">
import type { FormSubmitEvent } from '@nuxt/ui';
import { useMounted } from '@vueuse/core';
import * as yup from 'yup';
import { loginApi } from '~/api/auth';
import { useAuth } from '~/composables/useAuth';
import { useDeviceId } from '~/composables/useDeviceId';
import { useToastStore } from '~/stores/toast';
import type { ApiErrorWithStatus } from '~/types/api';
import { useLocalePath } from '#i18n';
import TwoFactorLogin from './TwoFactorLogin.vue';

const router = useRouter();
const localePath = useLocalePath();
const { login, handleSocialLogin, getRedirectUrl } = useAuth();
const { getOrCreateDeviceId } = useDeviceId();
const { t } = useI18n();
const toastStore = useToastStore();
const isLoading = ref(false);
// JS 接手前不讓表單送得出去（見 template 上方對 method="post" 的說明）
const isHydrated = useMounted();
const showTwoFactorInput = ref(false);
const provisionalToken = ref('');
const isPasswordVisible = ref(false);
const isLoginWithSpecifiedEmailMode = ref(false);

const formState = ref({
  email: '',
  password: '',
});

const schema = yup.object({
  email: yup
    .string()
    .required(t('pages.login.step2.validation.email.required'))
    .email(t('pages.login.step2.validation.email.invalid')),
  password: yup.string().required(t('pages.login.step2.validation.password.required')),
});

const emit = defineEmits(['toStep3']);

async function onSubmit(event: FormSubmitEvent<typeof formState.value>) {
  if (isLoading.value) return;

  try {
    isLoading.value = true;
    const deviceId = getOrCreateDeviceId();
    const response = await loginApi({
      email: event.data.email,
      password: event.data.password,
      device_id: deviceId,
    });

    if (response) {
      // Check if 2FA is enabled
      if (response.is_2fa_enabled && response.provisional_2fa_token) {
        // Store provisional token and show 2FA input
        provisionalToken.value = response.provisional_2fa_token;
        showTwoFactorInput.value = true;
      } else {
        await login(response.token, response.refresh_token);
      }
    } else {
      toastStore.error(t('pages.login.step2.loginFailed'));
    }
  } catch (error) {
    const apiError = error as ApiErrorWithStatus | Error;

    if ('message' in apiError && apiError.message) {
      toastStore.error(apiError.message);
    } else {
      toastStore.error(t('pages.login.step2.loginFailed'));
    }
  } finally {
    isLoading.value = false;
  }
}

const handleEnterSubmit = (event: KeyboardEvent) => {
  if (isLoading.value) return;
  const target = event.currentTarget as HTMLElement | null;
  const formElement = target?.closest('form');
  if (formElement) {
    formElement.dispatchEvent(
      new Event('submit', {
        bubbles: true,
        cancelable: true,
      })
    );
  }
};

async function handleTwoFactorSuccess(token: string, refreshToken: string) {
  await login(token, refreshToken);
}

function backToLogin() {
  showTwoFactorInput.value = false;
  provisionalToken.value = '';
}

// Check for Google OAuth 2FA callback on component mount
onMounted(() => {
  const route = useRoute();

  const is2FAEnabled = route.query.is_2fa_enabled;
  const provisionalToken2FA = route.query.provisional_2fa_token;

  if (is2FAEnabled === 'true' && provisionalToken2FA) {
    // Show 2FA input for Google OAuth callback
    provisionalToken.value = provisionalToken2FA as string;
    showTwoFactorInput.value = true;

    // Clear the query parameters from URL, but preserve err_code
    const { is_2fa_enabled, provisional_2fa_token, err_code, ...newQuery } = route.query;
    const cleanedQuery = { ...newQuery };
    // 如果有 err_code，保留它
    if (err_code) {
      cleanedQuery.err_code = err_code;
    }

    router.replace({
      path: route.path,
      query: cleanedQuery,
    });
  }

  const redirectDataObj = getRedirectUrl();
  const email = redirectDataObj?.query.email;
  if (email) {
    formState.value.email = email;
    isLoginWithSpecifiedEmailMode.value = true;
  }
});
</script>